/* eslint-disable camelcase */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import type { ReactUnityEventParameter } from "react-unity-webgl/distribution/types/react-unity-event-parameters";
import {
	type DiceData,
	type Player,
	type SocketActionData,
	type SocketHitData,
	type SocketMoveData,
	type SocketResultData,
	type SocketTurnData,
	type BoardData,
	type LastTurn,
	LudoSocketEventVariant,
	unityEvents,
	unityGameObjects,
} from "../../Types";

import {
	socketPlayersToUnitySquads,
	SocketStartPointToUnityTurn,
} from "../../helpers";
import { LUDO_GAME_PATH, unityActions } from "../../Constants";
import { useAblyRealtime, useSocket } from "@/hooks";
import { SocketMatchDataToBoardData } from "../../helpers/index";
import {
	sendMessageToReactNative,
	sendUnityProfileToReactNative,
} from "@/helpers";

interface Props {
	token: string;
	matchID: string;
	clientId: string;
}

function useLudo({ matchID, token, clientId }: Props) {
	const {
		unityProvider,
		loadingProgression,
		isLoaded,
		addEventListener,
		removeEventListener,
		sendMessage,
	} = useUnityContext({
		loaderUrl: LUDO_GAME_PATH.LOADER_URL,
		dataUrl: LUDO_GAME_PATH.DATA_URL,
		frameworkUrl: LUDO_GAME_PATH.FRAMEWORK_URL,
		codeUrl: LUDO_GAME_PATH.CODE_URL,
	});
	const [initialized, setInitialized] = useState(false);
	const [isConnected, setIsConnected] = useState(false);
	const { matchSocket, reconnect, boardData, boardCleaner } =
		useSocket<BoardData>({
			token,
			matchID,
			selector: SocketMatchDataToBoardData,
		});
	const { MatchPresentsHandler } = useAblyRealtime({
		clientID: clientId,
		matchID: matchID,
	});

	const loadingPercentage = useMemo(
		() => Math.round(loadingProgression * 100),
		[loadingProgression]
	);

	const getProfile = (id: number) => {
		try {
			const profile = boardData?.players_profiles.find(
				(item) => item.user_id === id
			);
			return profile;
		} catch (error) {
			console.log(error);
			setInitialized(false);
			sendMessageToReactNative("matchInvalid", {});
			throw error;
		}
	};
	const HandleDice = useCallback(() => {
		matchSocket.emit("user-game-action", {
			match_id: matchID,
			action_name: "ludo.roll_dice",
		});
	}, [matchSocket, matchID]);

	const HandleMove = useCallback(
		(...parameters: Array<ReactUnityEventParameter>) => {
			const [piceID] = parameters;

			matchSocket.emit("user-game-action", {
				match_id: matchID,
				action_name: "ludo.select_piece",
				data: {
					piece_id: piceID ? +piceID : 0,
				},
			});
		},
		[matchSocket, matchID]
	);
	const HandleSocketMove = useCallback(
		(socketData: SocketActionData<SocketMoveData>) => {
			if (boardData) {
				const player = boardData?.players.find(
					(player) => player.playerId === socketData.data.playerId
				);

				sendMessage(
					player?.squad ?? "",
					"MovePlayerById",
					socketData.data.pieceId.toString()
				);
			}
		},
		[boardData, sendMessage]
	);
	const HandleActivePice = useCallback(
		(diceData: DiceData) => {
			sendMessage(
				unityGameObjects.DICE_BUTTON,
				unityActions.DiceHandler.changeDiceNumber,
				diceData.diceValue
			);
			if (
				boardData &&
				boardData.current_user_id === diceData.playerId &&
				!!diceData.options.length
			) {
				const player = boardData?.players.find(
					(player) => player.playerId === diceData.playerId
				);

				const SelectedSquad = socketPlayersToUnitySquads(
					player?.startPoint ?? 0
				);
				const ActivePaces = diceData.options
					.map((item) => item.pieceId)
					.join(",");

				sendMessage(
					SelectedSquad ?? "",
					unityActions.squadHandlers.ActivePaces,
					ActivePaces
				);
			}
		},
		[boardData, sendMessage]
	);
	const HandleSocketTurnUpdate = useCallback(
		(playerId: number, timer = 60000, lastTurn?: LastTurn) => {
			try {
				if (boardData) {
					setTimeout(() => {
						const player = boardData?.players.find(
							(player) => player.playerId === playerId
						);

						const SelectedTurn = SocketStartPointToUnityTurn(
							player?.startPoint ?? 0
						);

						sendMessage(
							unityGameObjects.GAME_MANAGER,
							unityActions.gameHandlers.changeTurn,
							SelectedTurn
						);
						sendMessage(
							unityGameObjects.DICE_BUTTON,
							boardData.current_user_id === playerId
								? "ActiveDice"
								: "DeactivateDice"
						);
						sendMessage(unityGameObjects.GAME_MANAGER, "TurnAllLightsOff");
						sendMessage(unityGameObjects.DICE_BUTTON, "SetTimer", timer);
						sendMessage(player?.squad ?? "", "SetLightOn");
						if (lastTurn && lastTurn.step === "MOVE_PIECE") {
							HandleActivePice({
								diceValue: boardData.LastTurn.diceValue,
								options: boardData.LastTurn.options,
								playerId: boardData.LastTurn.playerId,
							});
						}
					}, 1200);
				}
			} catch (error) {
				console.log(error);
			}
		},
		[boardData, sendMessage]
	);

	const HandleSocketHit = useCallback(
		(HitData: SocketActionData<SocketHitData>) => {
			if (boardData) {
				const player = boardData?.players.find(
					(player) => player.playerId === HitData.data.playerId
				);
				setTimeout(() => {
					sendMessage(
						player?.squad ?? "",
						unityActions.squadHandlers.HitPacesByID,
						HitData.data.pieceId.toString()
					);
				}, 700);
			}
		},
		[boardData, sendMessage]
	);
	const HandleSocketTimeOut = useCallback(
		(currentTurnPlayer: Player) => {
			if (boardData) {
				const player = boardData?.players.find(
					(player) => player.playerId === currentTurnPlayer.playerId
				);
				sendMessage(
					player?.squad ?? "",
					"SetTimeOuts",
					currentTurnPlayer.faultCount
				);
			}
		},
		[boardData, sendMessage]
	);
	const HandleSocketWon = useCallback(
		(resultData: SocketActionData<SocketResultData>) => {
			if (boardData) {
				const winner = resultData.data.results.find(
					(player) => player.status === "Won"
				);
				const player = boardData?.players.find(
					(player) => player.playerId === winner?.player_id
				);
				sendMessage(player?.squad ?? "", unityActions.squadHandlers.WonHandler);

				sendMessage(unityGameObjects.DICE_BUTTON, "ResetTimer");
				sendMessageToReactNative("endGame", { ...resultData.data });
			}
		},
		[boardData, sendMessage]
	);
	const HandleSocketLeft = useCallback(
		(playerData: SocketActionData<Player>) => {
			if (boardData) {
				const player = boardData?.players.find(
					(player) => player.playerId === playerData.data.playerId
				);
				sendMessage(player?.squad ?? "", "DeactivateSquad");
			}
		},
		[boardData, sendMessage]
	);

	// unity listeners
	useEffect(() => {
		if (isLoaded) {
			addEventListener(unityEvents.DICE_ROLLED, HandleDice);
			addEventListener(unityEvents.PLAYER_MOVED, HandleMove);
			addEventListener(
				unityEvents.PROFILE_CLICKED,
				sendUnityProfileToReactNative
			);
		}

		return () => {
			removeEventListener(unityEvents.DICE_ROLLED, HandleDice);
			removeEventListener(unityEvents.PLAYER_MOVED, HandleMove);
			removeEventListener(
				unityEvents.PROFILE_CLICKED,
				sendUnityProfileToReactNative
			);
		};
	}, [addEventListener, removeEventListener, HandleDice, HandleMove, isLoaded]);
	// socket
	useEffect(() => {
		function onDisconnect() {
			setIsConnected(false);
			setInitialized(false);
			boardCleaner();
			console.log("socket disconnected");
		}
		function onConnect() {
			setIsConnected(true);
			console.log("connected");
		}

		matchSocket.on("disconnect", onDisconnect);
		matchSocket.on("connect", onConnect);

		matchSocket.on("game-engine-event", (data: string) => {
			const socketData = JSON.parse(data);
			console.log(socketData);
			if (initialized) {
				switch ((socketData as SocketActionData).action) {
					case LudoSocketEventVariant.ROLLED_DICE: {
						const diceData = socketData as SocketActionData<DiceData>;

						HandleActivePice(diceData.data);

						break;
					}
					case LudoSocketEventVariant.TURN_UPDATE: {
						const TurnData = socketData as SocketActionData<SocketTurnData>;

						HandleSocketTurnUpdate(TurnData.data.currentTurnPlayer.playerId);

						break;
					}
					case LudoSocketEventVariant.PLAYER_MOVE: {
						const TurnData = socketData as SocketActionData<SocketMoveData>;

						HandleSocketMove(TurnData);

						break;
					}
					case LudoSocketEventVariant.PLAYER_HIT: {
						const TurnData = socketData as SocketActionData<SocketMoveData>;

						HandleSocketHit(TurnData);

						break;
					}
					case LudoSocketEventVariant.TIME_OUT: {
						const TurnData = socketData as SocketActionData<Player>;

						HandleSocketTimeOut(TurnData.data);

						break;
					}
					case LudoSocketEventVariant.END_GAME: {
						const gameResult = socketData as SocketActionData<SocketResultData>;

						HandleSocketWon(gameResult);

						break;
					}
					case LudoSocketEventVariant.PLAYER_LEFT: {
						const playerData = socketData as SocketActionData<Player>;

						HandleSocketLeft(playerData);

						break;
					}

					default:
						break;
				}
			}
		});
		MatchPresentsHandler();
		return () => {
			matchSocket.off();
			// matchSocket.disconnect();
		};
	}, [matchSocket, sendMessage, boardData, initialized]);
	// socket connector
	useEffect(() => {
		if (isLoaded && isConnected) {
			reconnect();
		}
	}, [isLoaded, reconnect, isConnected]);
	// initializer
	useEffect(() => {
		if (isLoaded && isConnected && boardData && !initialized) {
			sendMessage(
				unityGameObjects.GAME_MANAGER,
				"InitializeBoard",
				boardData.currentPlayerColor
			);
			sendMessage(
				unityGameObjects.GAME_MANAGER,
				"ActiveSquads",
				boardData.players.length
			);

			boardData.players.map((player) => {
				const piceList = player.pieces
					.map((item) => {
						if (item.pieceStatus === "OUT") return "OUT";
						if (item.pieceStatus === "WON") return "56";
						if (
							item.pieceStatus === "PLAYING" &&
							item.position?.type === "SIDE_ROAD"
						)
							return item.position?.index + 51;

						return item.position?.index;
					})
					.join(",");

				sendMessage(player.squad ?? "", "SquadInitializer", piceList);
				const profile = getProfile(player.playerId);
				sendMessage(
					player.squad ?? "",
					"ActiveProfile",
					JSON.stringify({
						...profile,
						profile_image: profile?.profile_image.replace(".svg", ".png"),
						TimeOutsCount: player.faultCount,
						username: profile?.full_name ?? profile?.username,
					})
				);
				if (player.isLeftTheGame) {
					sendMessage(player?.squad ?? "", "DeactivateSquad");
				}
			});

			HandleSocketTurnUpdate(
				boardData.LastTurn.playerId,
				boardData.remainingTime,
				boardData.LastTurn
			);

			sendMessageToReactNative("gameStarted", {});

			setInitialized(true);
		}
	}, [
		isLoaded,
		matchSocket.connected,
		boardData,
		sendMessage,
		HandleSocketTurnUpdate,
		isConnected,
		initialized,
	]);

	return {
		loadingPercentage,
		unityProvider,
		isLoaded,
		isConnected,
		initialized,
	};
}
export default useLudo;
